import { App } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type User from '../../interfaces/User';
import { UserService } from '../../services/userApiService';
import { useAppStore } from '../../store/store';
import { useAuth } from '../useAuth';

const userService = new UserService();

/** sessionStorage flag: impersonation email was not queued; shown after reload. */
export const IMPERSONATION_EMAIL_NOT_SENT_KEY = 'impersonationEmailNotSent';

/**
 * Return signature for the useOnBehalfOf hook
 */
export interface UseOnBehalfOfReturn {
  /** Whether current session is on behalf of another user */
  isOnBehalfOfUser: boolean;
  /** The on behalf of user's information, or null if not on behalf of user */
  onBehalfOfUser: User | null;
  /**
   * Start acting on behalf of the target user
   * @param userId ID of the user to act on behalf of
   * @param reason Optional reason for auditing
   */
  enterOnBehalfOfUser: (userId: number, reason?: string) => Promise<void>;
  /**
   * Stop acting on behalf of user and revert to original user
   */
  exitOnBehalfOfUser: () => Promise<void>;
}

/**
 * Custom React hook for managing "On Behalf Of" user functionality.
 *
 * Provides the ability for an admin to act on behalf of another user,
 * update authentication tokens, track on behalf of state and time remaining,
 * and handle completion of on behalf of session (reversion) with proper error handling and notifications.
 */
export const useOnBehalfOf = (): UseOnBehalfOfReturn => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { getUserInfo } = useAuth();

  // Zustand selectors for on behalf of related state and actions
  const onBehalfOfUser = useAppStore((s) => s.onBehalfOfUser);
  const setOnBehalfOfData = useAppStore((s) => s.setOnBehalfOfData);
  const clearOnBehalfOfData = useAppStore((s) => s.clearOnBehalfOfData);
  const setCustomTokens = useAppStore((s) => s.setCustomTokens);
  const setIsModalLoading = useAppStore((s) => s.setIsModalLoading);

  /**
   * Indicates if on behalf of user is currently active.
   * Only true if user data is set.
   */
  const isOnBehalfOfUser = useMemo(() => onBehalfOfUser !== null, [onBehalfOfUser]);

  // After reload: surface email-not-sent warning that could not survive the reload toast wipe
  useEffect(() => {
    if (sessionStorage.getItem(IMPERSONATION_EMAIL_NOT_SENT_KEY) === null) {
      return;
    }
    sessionStorage.removeItem(IMPERSONATION_EMAIL_NOT_SENT_KEY);
    message.warning({
      content: t('errors.impersonatedUserNotNotified'),
    });
  }, [message, t]);

  /**
   * Initiates on behalf of for a specific user (as an admin).
   * Updates authentication tokens and user info without page reload.
   * Notifies user of failure on error.
   */
  const enterOnBehalfOfUser = useCallback(
    async (userId: number, reason?: string) => {
      setIsModalLoading(true);
      try {
        // Request backend to enter on behalf of the selected user
        const response = await userService.enterOnBehalfOfUser({
          targetUserId: userId,
          reason: reason,
        });

        // Explicit false only: missing field (older BE) = treat as notified, no warning.
        // Flag survives reload; toast shown in useEffect after boot.
        if (response.impersonatedUserNotified === false) {
          sessionStorage.setItem(IMPERSONATION_EMAIL_NOT_SENT_KEY, '1');
        }

        // Compose the User object from backend response fields
        const user: User = {
          id: response.impersonatedUserId,
          email: response.impersonatedUserEmail,
          firstName: response.impersonatedUserName.split(' ')[0] || '',
          lastName: response.impersonatedUserName.split(' ').slice(1).join(' ') || '',
        };

        // Update tokens in store (both token and refresh token)
        setCustomTokens({
          token: response.token,
          rToken: response.rToken,
        });

        // Store on behalf of session data (user info)
        // Note: expiresAt is kept for backward compatibility but not used for timeout display
        setOnBehalfOfData(response.expiresAt, user);

        window.location.reload();
      } catch (error: unknown) {
        // Log and display error if entering on behalf of user fails
        console.error('Failed to enter on behalf of user:', error);
        message.error({
          content: t('errors.enterOnBehalfOfUserFailed') || 'Failed to enter on behalf of user',
        });
        throw error;
      } finally {
        setIsModalLoading(false);
      }
    },
    [setCustomTokens, setOnBehalfOfData, setIsModalLoading, t, message],
  );

  /**
   * Reverts session out of on behalf of, returning to original user/admin context.
   * Updates tokens and clears on behalf of state without page reload.
   * Notifies user on error.
   */
  const exitOnBehalfOfUser = useCallback(async () => {
    try {
      // Request backend to exit on behalf of session
      const response = await userService.exitOnBehalfOfUser();

      // Restore regular (admin/original) tokens from backend response
      setCustomTokens({
        token: response.token,
        rToken: response.rToken,
      });

      // Remove all on behalf of data from the store
      clearOnBehalfOfData();

      // Update user info to reflect original admin user
      // Now we can save authenticatedUserEmail again since we're back to the original user
      //await getUserInfo({ redirectHome: false, saveAuthenticatedUserEmail: true });

      window.location.reload();
    } catch (error: unknown) {
      // Log and notify on failure to exit on behalf of
      console.error('Failed to exit on behalf of user:', error);
      message.error({
        content: t('errors.exitOnBehalfOfUserFailed') || 'Failed to exit on behalf of user',
      });
      throw error;
    }
  }, [setCustomTokens, clearOnBehalfOfData, getUserInfo, t, message]);

  return {
    isOnBehalfOfUser,
    onBehalfOfUser,
    enterOnBehalfOfUser,
    exitOnBehalfOfUser,
  };
};
